import { useTranslation } from 'react-i18next';
import { useApi } from '@hero/context';
import type {
  UseAppEarlyAccessApi,
  UseAppIssueWaybillBayanApi,
  UseAppLogistiGoodTypesApi,
  UseAppLogistiInstallApi,
  UseAppLogistiSettingsApi,
  UseAppLogistiUninstallApi,
  UseAppsApi,
  UseAppsInstalledApi,
  UseAppWaybillBayanPdfApi,
  UseAppWaybillBayanStatusApi,
} from './types';
import { useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get the Apps & Integrations catalog.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn.
 *
 * Hook API: {@link UseAppsApi}
 */
export const useAppsApi: UseAppsApi = apiConfig => {
  const api = useApi('carrier');
  const { i18n } = useTranslation();
  return useQueryAPI({
    entity: 'app',
    apiFn: api.apps.getAll,
    query: undefined,
    // Catalog strings are localized server-side — bust cache per language.
    queryKeyScope: `catalog-${i18n.language}`,
    apiConfig,
  });
};

/**
 * Apply for early access / register interest in an app.
 * API from ApiProvider (useApi('carrier')); pass apiConfig for onSuccess/onError.
 *
 * Hook API: {@link UseAppEarlyAccessApi}
 */
export const useAppEarlyAccessApi: UseAppEarlyAccessApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'app',
    apiFn: api.apps.applyEarlyAccess,
    apiConfig,
  });
};

/**
 * The caller's installs. The provider account id arrives masked, so this is
 * safe to render as-is.
 *
 * Also the answer to "can this tenant file right now" — every row carries
 * `filingMode` and `canIssue`. There was a second query for that
 * (`logistiCapability`), from when platform filing wrote no install row; it
 * meant every order screen paid for two requests to answer one question, and
 * whichever surface read the other one disagreed.
 *
 * The `queryKeyScope` matters: without it this shares a cache key with
 * {@link useAppsApi} and the two payloads overwrite each other.
 *
 * Hook API: {@link UseAppsInstalledApi}
 */
export const useAppsInstalledApi: UseAppsInstalledApi = apiConfig => {
  const api = useApi('carrier');
  return useQueryAPI({
    entity: 'app',
    apiFn: api.apps.getInstalled,
    query: undefined,
    queryKeyScope: 'installed',
    apiConfig,
  });
};

/**
 * Bayan cargo types the Org may put on a waybill, leaves only.
 *
 * Feeds the "default cargo type" setting and the per-order picker. Only
 * fetched when Logisti is connected — the endpoint 404s otherwise, and every
 * Org without the app would pay for a failed request on page load.
 *
 * Hook API: {@link UseAppLogistiGoodTypesApi}
 */
export const useAppLogistiGoodTypesApi: UseAppLogistiGoodTypesApi = apiConfig => {
  const api = useApi('carrier');
  const installed = useAppsInstalledApi();
  const isConnected = (installed.data ?? []).some(app => app.appId === 'logisti' && app.enabled);

  return useQueryAPI({
    entity: 'app',
    apiFn: api.apps.logistiGoodTypes,
    query: undefined,
    queryKeyScope: 'good-types',
    isEnabled: () => isConnected,
    apiConfig,
  });
};

/**
 * Install the Org's Bayan account.
 *
 * The backend calls Bayan to verify the account id before saving, so an
 * `onError` here means the credential itself was rejected — worth surfacing
 * verbatim rather than as a generic failure.
 *
 * Hook API: {@link UseAppLogistiInstallApi}
 */
export const useAppLogistiInstallApi: UseAppLogistiInstallApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'app',
    apiFn: api.apps.logistiInstall,
    apiConfig,
  });
};

/**
 * Uninstall Logisti.
 *
 * Hook API: {@link UseAppLogistiUninstallApi}
 */
export const useAppLogistiUninstallApi: UseAppLogistiUninstallApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'app',
    apiFn: api.apps.logistiUninstall,
    apiConfig,
  });
};

/**
 * Update trigger / commercial-term settings.
 *
 * Hook API: {@link UseAppLogistiSettingsApi}
 */
export const useAppLogistiSettingsApi: UseAppLogistiSettingsApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'app',
    apiFn: api.apps.logistiUpdateSettings,
    apiConfig,
  });
};

/**
 * Government-waybill state for one order, including outstanding blockers.
 *
 * Disabled until `orderId` is known so the panel doesn't fire a request while
 * the parent order is still loading.
 *
 * Hook API: {@link UseAppWaybillBayanStatusApi}
 */
export const useAppWaybillBayanStatusApi: UseAppWaybillBayanStatusApi = (orderId, apiConfig) => {
  const api = useApi('carrier');
  return useQueryAPI({
    entity: 'app',
    apiFn: api.apps.logistiWaybillStatus,
    query: orderId ? { orderId } : undefined,
    isEnabled: () => Boolean(orderId),
    apiConfig,
  });
};

/**
 * Issue or retry the government waybill.
 *
 * Idempotent on the backend — an order that already has a trip returns its
 * existing state rather than creating a second document.
 *
 * Hook API: {@link UseAppIssueWaybillBayanApi}
 */
export const useAppIssueWaybillBayanApi: UseAppIssueWaybillBayanApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'app',
    apiFn: api.apps.logistiIssueWaybill,
    apiConfig,
  });
};

/**
 * Fetch the Bayan-issued PDF as base64.
 *
 * A mutation rather than a query because it's an on-demand fetch of a payload
 * too large to want cached, and Bayan re-renders it per call.
 *
 * Hook API: {@link UseAppWaybillBayanPdfApi}
 */
export const useAppWaybillBayanPdfApi: UseAppWaybillBayanPdfApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'app',
    apiFn: api.apps.logistiWaybillPdf,
    apiConfig,
  });
};
